import React, { useState, useEffect } from 'react';
import { toPairs, pick, isNil } from 'ramda';
import debounce from 'lodash.debounce';
import { Form, Row, Col, Input, Button, Select, Card } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { QuestionCircleOutlined, LinkOutlined } from '@ant-design/icons';
import { useAccount } from 'ShorePay/hooks/account';
import type { FieldData } from 'Common/types';
import {
  useSaveSettings,
  useStripeDashboardLink,
} from 'ShorePay/hooks/settings';
import PhoneInput from 'Common/Components/PhoneInput';
import InfoBox from 'Common/Components/InfoBox';
import TemplateCard from 'Common/Components/TemplateCard';
import { extractDial } from 'utils/formatPhone';

const SelectOption = Select.Option;

const AccountSettings = () => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [isFormTouched, setIsFormTouched] = useState(false);
  const accQuery = useAccount();
  const accountSettingsMutation = useSaveSettings();
  const dashboardQuery = useStripeDashboardLink();
  const { stripe_account_data: data } = accQuery.data ?? {};
  const { url: stripe_dashboard_link } = dashboardQuery.data ?? {};

  useEffect(() => {
    form.resetFields();
    if (data) {
      const fields: FieldData[] = [
        ...toPairs(pick(['business_profile', 'settings'], data)).map((f) => ({
          name: f[0],
          value: f[1],
        })),
        {
          name: ['business_profile', 'support_phone'],
          value:
            data.business_profile.support_phone &&
            extractDial(data.business_profile.support_phone),
        },
      ];
      form.setFields(fields);
      form.setFieldsValue({
        business_profile: {
          support_address: {
            country: data.business_profile?.support_address?.country || 'DE',
          },
        },
      });
    }

    return () => setIsFormTouched(false);
  }, [form, data]);

  const handleFormSubmission = (values: any) => {
    const formValues = { ...values };
    setIsFormTouched(false);
    if (form.getFieldValue(['business_profile', 'support_phone']) !== null) {
      const { dial, number } = form.getFieldValue([
        'business_profile',
        'support_phone',
      ]);
      if (!isNil(dial) && !isNil(number)) {
        formValues.business_profile.support_phone = dial + number;
      }
    }
    accountSettingsMutation.mutate(formValues);
  };

  return (
    <Row css="padding: 12px">
      <Col>
        <Form
          form={form}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          onValuesChange={debounce(() => {
            if (!isFormTouched) {
              setIsFormTouched(form.isFieldsTouched());
              form.submit();
            }
          }, 2000)}
          onFinish={handleFormSubmission}
          css={`
            margin-top: -20px;
            .ant-row {
              justify-content: center;
            }
            #business_profile_support_address_line1,
            #business_profile_support_address_line2,
            #business_profile_name {
              margin-bottom: 15px;
            }
          `}
        >
          <Row gutter={[16, 16]} css="margin:0px !important">
            <Col xl={12} lg={26}>
              <TemplateCard
                title={intl.formatMessage({
                  defaultMessage: 'Support Address Settings',
                })}
              >
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <InfoBox
                      css="margin-top:16px"
                      text={intl.formatMessage({
                        defaultMessage:
                          'The support address is used in communication with your customers when they have questions about transactions with you.',
                      })}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <Form.Item
                      name={['settings', 'payments', 'statement_descriptor']}
                      label={intl.formatMessage({
                        defaultMessage: 'Bank Statement Description',
                      })}
                      rules={[
                        {
                          max: 22,
                          message: intl.formatMessage({
                            defaultMessage:
                              'The text can not be longer than 22 characters',
                          }),
                        },
                      ]}
                      tooltip={{
                        title: 'Bank Statement',
                        icon: <QuestionCircleOutlined />,
                      }}
                      css="margin-top:16px"
                    >
                      <Input data-testid="bank-stmt" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <Form.Item
                      label={intl.formatMessage({
                        defaultMessage: 'Business URL',
                      })}
                      name={['business_profile', 'support_url']}
                      css="margin-top:16px"
                    >
                      <Input type="url" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <Form.Item
                      name={['business_profile', 'support_phone']}
                      label={intl.formatMessage({
                        defaultMessage: 'Phone Number',
                      })}
                      shouldUpdate
                      css="margin-top:16px"
                    >
                      <PhoneInput />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <Form.Item
                      label={intl.formatMessage({
                        defaultMessage: 'Email Address',
                      })}
                      name={['business_profile', 'support_email']}
                      rules={[
                        {
                          type: 'email',
                          message: intl.formatMessage({
                            defaultMessage: 'Email address is invalid',
                          }),
                        },
                      ]}
                      css="margin-top:16px"
                    >
                      <Input type="email" data-testid="support-email" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <Form.Item
                      name={['business_profile', 'name']}
                      label={intl.formatMessage({
                        defaultMessage: 'Support Address',
                      })}
                      tooltip={{
                        title: 'Company name and Address details',
                        icon: <QuestionCircleOutlined />,
                      }}
                      css="margin-top:16px"
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          defaultMessage: 'Company Name',
                        })}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <Form.Item
                      name={['business_profile', 'support_address', 'line1']}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          defaultMessage: 'Street and Number',
                        })}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <Form.Item
                      name={['business_profile', 'support_address', 'line2']}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          defaultMessage: 'Additional Information',
                        })}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xl={8} lg={9} sm={9}>
                    <Form.Item
                      style={{
                        marginRight: '15px',
                      }}
                      name={[
                        'business_profile',
                        'support_address',
                        'postal_code',
                      ]}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          defaultMessage: 'Zip Code',
                        })}
                      />
                    </Form.Item>
                  </Col>
                  <Col xl={8} lg={9} sm={9}>
                    <Form.Item
                      name={['business_profile', 'support_address', 'city']}
                    >
                      <Input
                        placeholder={intl.formatMessage({
                          defaultMessage: 'City',
                        })}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <Form.Item
                      style={{
                        marginTop: '15px',
                      }}
                      name={['business_profile', 'support_address', 'country']}
                    >
                      <Input
                        disabled
                        placeholder={intl.formatMessage({
                          defaultMessage: 'DE',
                        })}
                        css="margin-bottom:40px"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </TemplateCard>
            </Col>
            <Col xl={12} lg={26}>
              <Card
                title={intl.formatMessage({
                  defaultMessage: 'Account Settings',
                })}
                style={{ height: 380 }}
              >
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <Form.Item
                      label={intl.formatMessage({
                        defaultMessage: 'Payout Cycle',
                      })}
                      name={['settings', 'payouts', 'schedule', 'interval']}
                      css="margin-top:20px"
                    >
                      <Select data-testid="payout-cycle">
                        <SelectOption value="daily">
                          <FormattedMessage defaultMessage="Daily Payouts" />
                        </SelectOption>
                        <SelectOption value="weekly">
                          <FormattedMessage defaultMessage="Weekly Payouts" />
                        </SelectOption>
                        <SelectOption value="monthly">
                          <FormattedMessage defaultMessage="Monthly Payouts" />
                        </SelectOption>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <InfoBox
                      css="margin-top:20px;margin-bottom:20px"
                      text={intl.formatMessage({
                        defaultMessage:
                          'Shore Pay is powered by Stripe. Some information can only be edited on the Stripe dashboard directly. Click on the button below to access it.',
                      })}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xl={16} lg={18} sm={18}>
                    <a
                      href={stripe_dashboard_link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        data-testid="edit-btn"
                        block
                        icon={<LinkOutlined />}
                      >
                        <FormattedMessage defaultMessage="Edit information on Stripe" />
                      </Button>
                    </a>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};
export default AccountSettings;
